import { useMemo, useState } from 'react';
import { useTemplates, useTenants, useCreateDashboard, useTemplate, useCluvioDashboards } from '@/api/hooks';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { Input } from '@/components/Input';
import { CluvioOptionInput, CluvioMultiOptionInput } from '@/components/CluvioOptionInput';
import { PageHeader, Skeleton, EmptyState, Field } from '@/components/primitives';
import type { ComboboxOption } from '@/components/Combobox';
import { presetsForFilterKey, isMultiControl } from '@/lib/cluvio';
import { toast } from 'react-hot-toast';
import { Loader2, PlusCircle, Search, Check, LayoutTemplate, Users } from 'lucide-react';
import { cn } from '@/lib/utils';

function PickerCard({
  title,
  search,
  onSearch,
  searchPlaceholder,
  children,
}: {
  title: string;
  search: string;
  onSearch: (v: string) => void;
  searchPlaceholder: string;
  children: React.ReactNode;
}) {
  return (
    <Card className="p-5 animate-enter">
      <h2 className="text-sm font-semibold mb-3">{title}</h2>
      <div className="relative mb-2">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input value={search} onChange={(e) => onSearch(e.target.value)} placeholder={searchPlaceholder} className="pl-9" />
      </div>
      <div className="max-h-64 overflow-auto divide-y divide-border/60">{children}</div>
    </Card>
  );
}

export function DashboardCreatorPage() {
  const { data: templates, isLoading: templatesLoading } = useTemplates();
  const { data: tenants, isLoading: tenantsLoading } = useTenants();
  const createMutation = useCreateDashboard();

  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [selectedTenantId, setSelectedTenantId] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [sortOrder, setSortOrder] = useState('0');
  const [fixedParams, setFixedParams] = useState<Record<string, ComboboxOption | ComboboxOption[]>>({});
  const [varOverrides, setVarOverrides] = useState<Record<string, any>>({});
  const [templateSearch, setTemplateSearch] = useState('');
  const [tenantSearch, setTenantSearch] = useState('');

  const { data: fullTemplate } = useTemplate(selectedTemplateId || '');
  const selectedTemplate = templates?.find((t) => t.id === selectedTemplateId);
  const selectedTenant = tenants?.find((t) => t.id === selectedTenantId);

  // Cluvio controlType per fixed filter (for multi-select cardinality)
  const { data: cluvioDashboards } = useCluvioDashboards();
  const controlTypeByFilterId = useMemo(() => {
    const dashId = fullTemplate?.cluvioDashboard?.id;
    const filters = cluvioDashboards?.find((d) => d.id === dashId)?.filters ?? [];
    return Object.fromEntries(filters.map((f) => [f.id, f.controlType ?? '']));
  }, [cluvioDashboards, fullTemplate]);

  if (templatesLoading || tenantsLoading) {
    return (
      <div className="max-w-2xl mx-auto space-y-4">
        <PageHeader title="Create Dashboard" description="Instantiate a template for a tenant." />
        <Card className="p-5 space-y-3">
          <Skeleton className="h-9 w-full" />
          <Skeleton className="h-14 w-full" />
          <Skeleton className="h-14 w-full" />
        </Card>
      </div>
    );
  }

  const handleSubmit = async () => {
    if (!selectedTemplate || !selectedTenant) return;
    const fixedParameters = Object.entries(fixedParams).flatMap(([key, val]): Record<string, unknown>[] => {
      if (Array.isArray(val)) {
        return val.length
          ? [{ key, label: key, values: val.map((o) => ({ label: o.label, value: o.value })) }]
          : [];
      }
      return val.value ? [{ key, label: val.label || key, value: { label: val.label, value: val.value } }] : [];
    });

    const variableFilterOverrides = Object.entries(varOverrides).map(([key, val]) => ({
      key,
      ...val,
    }));

    try {
      await createMutation.mutateAsync({
        cluvioDashboardID: fullTemplate?.cluvioDashboard?.id || '',
        sharingToken: fullTemplate?.cluvioSharingToken?.token || '',
        name: name || selectedTemplate.defaultDashboardName || selectedTemplate.name,
        sortOrder: Number(sortOrder),
        description,
        typeOfTenantID: selectedTemplate.typeOfTenantID,
        tenantID: selectedTenant.id,
        templateID: selectedTemplate.id,
        fixedParameters,
        variableFilterOverrides,
      });
      toast.success(`Dashboard created for ${selectedTenant.name}`);
      setSelectedTemplateId(null);
      setSelectedTenantId(null);
      setName('');
      setDescription('');
      setSortOrder('0');
      setFixedParams({});
      setVarOverrides({});
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Creation failed');
    }
  };

  const filteredTemplates = (templates || [])
    .filter((t) => t.name.toLowerCase().includes(templateSearch.toLowerCase()))
    .sort((a, b) => a.name.localeCompare(b.name));

  const filteredTenants = (tenants || [])
    .filter((t) => t.typeOfTenantID === selectedTemplate?.typeOfTenantID)
    .filter((t) => t.name.toLowerCase().includes(tenantSearch.toLowerCase()))
    .sort((a, b) => a.name.localeCompare(b.name));

  return (
    <div className="max-w-2xl mx-auto space-y-4">
      <PageHeader title="Create Dashboard" description="Instantiate a template for a tenant." />

      <PickerCard
        title="1. Select Template"
        search={templateSearch}
        onSearch={setTemplateSearch}
        searchPlaceholder="Search templates..."
      >
        {filteredTemplates.length === 0 ? (
          <EmptyState icon={LayoutTemplate} title="No templates found" description="Try a different search term." />
        ) : (
          filteredTemplates.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => {
                setSelectedTemplateId(t.id);
                setFixedParams({});
                setVarOverrides({});
              }}
              className={cn(
                'w-full text-left px-2 py-2.5 transition-colors',
                selectedTemplateId === t.id ? 'bg-accent/60' : 'hover:bg-muted/50'
              )}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="text-sm font-medium truncate">{t.name}</div>
                {selectedTemplateId === t.id && <Check className="h-4 w-4 text-primary shrink-0" />}
              </div>
              <div className="text-xs text-muted-foreground mt-0.5">{t.typeOfTenantID}</div>
            </button>
          ))
        )}
      </PickerCard>

      {selectedTemplate && (
        <PickerCard
          title="2. Select Tenant"
          search={tenantSearch}
          onSearch={setTenantSearch}
          searchPlaceholder={`Search ${selectedTemplate.typeOfTenantID} tenants...`}
        >
          {filteredTenants.length === 0 ? (
            <EmptyState
              icon={Users}
              title="No matching tenants"
              description={`No tenants of type ${selectedTemplate.typeOfTenantID} match your search.`}
            />
          ) : (
            filteredTenants.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setSelectedTenantId(t.id)}
                className={cn(
                  'w-full text-left px-2 py-2.5 transition-colors',
                  selectedTenantId === t.id ? 'bg-accent/60' : 'hover:bg-muted/50'
                )}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="text-sm font-medium truncate">{t.name}</div>
                  {selectedTenantId === t.id && <Check className="h-4 w-4 text-primary shrink-0" />}
                </div>
              </button>
            ))
          )}
        </PickerCard>
      )}

      {selectedTemplate && selectedTenant && (
        <>
          <Card className="p-5 space-y-3 animate-enter">
            <h2 className="text-sm font-semibold">3. Dashboard Details</h2>
            <Field label="Name" hint={`Defaults to "${selectedTemplate.defaultDashboardName || selectedTemplate.name}"`}>
              <Input value={name} onChange={(e) => setName(e.target.value)} />
            </Field>
            <Field label="Description">
              <Input value={description} onChange={(e) => setDescription(e.target.value)} />
            </Field>
            <Field label="Sort order">
              <Input type="number" value={sortOrder} onChange={(e) => setSortOrder(e.target.value)} />
            </Field>
          </Card>

          {selectedTemplate.fixedFilters.length > 0 && (
            <Card className="p-5 space-y-3 animate-enter">
              <div>
                <h2 className="text-sm font-semibold">4. Fixed Parameters</h2>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Values locked into this dashboard. Suggestions come from Cluvio; custom values are allowed.
                </p>
              </div>
              {selectedTemplate.fixedFilters.map((f) => {
                const presets = presetsForFilterKey(f.key);
                const multi = !presets && isMultiControl(f.cluvioFilterID ? controlTypeByFilterId[f.cluvioFilterID] : undefined);
                const current = fixedParams[f.key];
                return (
                  <Field
                    key={f.key}
                    label={f.label}
                    hint={
                      presets
                        ? 'Time range preset — pick one or type a custom token (e.g. 7_full_day).'
                        : multi
                          ? 'Multiple values allowed.'
                          : undefined
                    }
                  >
                    {multi ? (
                      <CluvioMultiOptionInput
                        filterId={f.cluvioFilterID}
                        values={Array.isArray(current) ? current : []}
                        onChange={(v) => setFixedParams({ ...fixedParams, [f.key]: v })}
                        placeholder={`Values for ${f.label}…`}
                      />
                    ) : (
                      <CluvioOptionInput
                        filterId={f.cluvioFilterID}
                        presets={presets}
                        value={Array.isArray(current) ? undefined : current}
                        onChange={(opt) => setFixedParams({ ...fixedParams, [f.key]: opt })}
                        placeholder={presets ? 'Select a preset…' : `Value for ${f.label}…`}
                      />
                    )}
                  </Field>
                );
              })}
            </Card>
          )}

          <Button
            onClick={handleSubmit}
            disabled={createMutation.isPending}
            className="w-full"
          >
            {createMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <PlusCircle className="h-4 w-4 mr-2" />
            )}
            Create Dashboard for {selectedTenant.name}
          </Button>
        </>
      )}
    </div>
  );
}
